import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { 
  HookHouseTemplate, 
  TemplateConfig, 
  ImportStrategy 
} from '@/types/template.types';
import {
  generateTemplateId,
  loadTemplates,
  loadTemplatesFromStorage,
  addTemplate as apiAddTemplate,
  updateTemplate as apiUpdateTemplate,
  deleteTemplate as apiDeleteTemplate,
  exportTemplates,
  exportSingleTemplate,
  parseImportFile,
  mergeTemplates
} from '@utils/templateStorage';

interface TemplateContextType {
  templates: HookHouseTemplate[];
  isLoading: boolean;
  error: string | null;
  
  // CRUD operations
  createTemplate: (name: string, description: string, icon: string, tags: string[], config: TemplateConfig) => HookHouseTemplate;
  updateTemplate: (id: string, updates: Partial<Omit<HookHouseTemplate, 'id' | 'createdAt'>>) => void;
  deleteTemplate: (id: string) => void;
  getTemplate: (id: string) => HookHouseTemplate | undefined;
  
  // Import/Export
  exportAll: () => void;
  exportOne: (id: string) => void;
  importFromFile: (file: File, strategy: ImportStrategy) => Promise<{ success: boolean; count: number; error?: string }>;
  
  // Utility
  clearAll: () => void;
  clearError: () => void;
}

const TemplateContext = createContext<TemplateContextType | null>(null);

// Fix: explicitly allow optional children to fix JSX mapping issue in some TypeScript environments
export function TemplateProvider({ children }: { children?: ReactNode }) {
  const [templates, setTemplates] = useState<HookHouseTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load templates on mount (async API with localStorage fallback)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const loaded = await loadTemplates();
        if (!cancelled) {
          setTemplates(loaded);
          setIsLoading(false);
        }
      } catch {
        if (!cancelled) {
          setTemplates(loadTemplatesFromStorage());
          setIsLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Create a new template
  const createTemplate = useCallback((
    name: string,
    description: string,
    icon: string,
    tags: string[],
    config: TemplateConfig
  ): HookHouseTemplate => {
    const now = new Date().toISOString();
    const newTemplate: HookHouseTemplate = {
      id: generateTemplateId(),
      name,
      description,
      icon,
      tags,
      createdAt: now,
      updatedAt: now,
      config
    };

    setTemplates(prev => [...prev, newTemplate]);

    // Persist to API in background
    apiAddTemplate(newTemplate).then(serverTemplate => {
      if (serverTemplate.id !== newTemplate.id) {
        setTemplates(prev => prev.map(t => t.id === newTemplate.id ? serverTemplate : t));
      }
    }).catch(() => {});

    return newTemplate;
  }, []);

  // Update an existing template
  const updateTemplate = useCallback((
    id: string,
    updates: Partial<Omit<HookHouseTemplate, 'id' | 'createdAt'>>
  ): void => {
    setTemplates(prev => prev.map(t =>
      t.id === id ? { ...t, ...updates, updatedAt: new Date().toISOString() } : t
    ));

    // Persist to API in background
    apiUpdateTemplate(id, updates as Partial<HookHouseTemplate>).catch(() => {});
  }, []);

  // Delete a template
  const deleteTemplate = useCallback((id: string): void => {
    setTemplates(prev => prev.filter(t => t.id !== id));

    // Persist to API in background
    apiDeleteTemplate(id).catch(() => {});
  }, []);

  // Get a template by ID
  const getTemplate = useCallback((id: string): HookHouseTemplate | undefined => {
    return templates.find(t => t.id === id);
  }, [templates]);

  // Export all templates
  const exportAll = useCallback(() => {
    if (templates.length === 0) {
      setError('No templates to export');
      return;
    }
    exportTemplates(templates);
  }, [templates]);

  // Export single template
  const exportOne = useCallback((id: string) => {
    const template = templates.find(t => t.id === id);
    if (template) {
      exportSingleTemplate(template);
    } else {
      setError('Template not found');
    }
  }, [templates]);

  // Import from file
  const importFromFile = useCallback(async (
    file: File,
    strategy: ImportStrategy
  ): Promise<{ success: boolean; count: number; error?: string }> => {
    try {
      const content = await file.text();
      const parsed = parseImportFile(content);

      if (!parsed) {
        return { success: false, count: 0, error: 'Invalid file format' };
      }

      if (parsed.length === 0) {
        return { success: false, count: 0, error: 'No valid templates found in file' };
      }

      const merged = mergeTemplates(templates, parsed, strategy);
      const addedCount = merged.length - templates.length;

      // Persist to DB: determine which templates are new vs updated
      const existingIds = new Set(templates.map(t => t.id));

      // For replace_all, delete templates that were removed
      if (strategy === 'replace_all') {
        const mergedIds = new Set(merged.map(t => t.id));
        for (const t of templates) {
          if (!mergedIds.has(t.id)) {
            apiDeleteTemplate(t.id).catch(() => {});
          }
        }
      }

      // POST new templates and PUT updated ones
      for (const t of merged) {
        if (!existingIds.has(t.id)) {
          apiAddTemplate(t).catch(() => {});
        } else if (strategy === 'merge_overwrite' || strategy === 'replace_all') {
          // Only update if this template came from the import (matched by name)
          if (parsed.some(p => p.name.toLowerCase() === t.name.toLowerCase())) {
            apiUpdateTemplate(t.id, t).catch(() => {});
          }
        }
      }

      setTemplates(merged);
      return { success: true, count: strategy === 'replace_all' ? parsed.length : addedCount };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Import failed';
      return { success: false, count: 0, error: message };
    }
  }, [templates]);

  // Clear all templates
  const clearAll = useCallback(() => {
    setTemplates([]);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return (
    <TemplateContext.Provider value={{
      templates,
      isLoading,
      error,
      createTemplate,
      updateTemplate,
      deleteTemplate,
      getTemplate,
      exportAll,
      exportOne,
      importFromFile,
      clearAll,
      clearError
    }}>
      {children}
    </TemplateContext.Provider>
  );
}

export function useTemplates() {
  const context = useContext(TemplateContext);
  if (!context) {
    throw new Error('useTemplates must be used within TemplateProvider');
  }
  return context;
}